import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { StatusBadge } from "./StatusBadge";
import { ConfirmDialog } from "./ConfirmDialog";
import { DateFilterPicker } from "./DateFilterPicker";
import { formatDate } from "../../lib/dateUtils";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "registered", label: "Registered" },
  { value: "matched", label: "Matched" },
  { value: "cancelled", label: "Cancelled" },
];

const CONSENT_LABELS = { hand_holding: "Hand holding", hugging: "Hugging", sitting_close: "Sitting close", arm_around_shoulder: "Arm around shoulder", other: "Other" };
const formatConsent = (consent) => {
  const active = Object.keys(CONSENT_LABELS).filter((k) => consent?.[k]);
  return active.length ? active.map((k) => CONSENT_LABELS[k]).join(", ") : "No physical contact";
};

export const ApplicationsTab = () => {
  const [applications, setApplications] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(null);

  const load = () => {
    api
      .get("/admin/applications", { params: { status: status || undefined, search: search || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined } })
      .then(({ data }) => setApplications(data));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search, dateFrom, dateTo]);

  const cancelApplication = async (id) => {
    try {
      await api.put(`/admin/applications/${id}/cancel`);
      toast.success("Application cancelled");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't cancel application");
    }
  };

  return (
    <div data-testid="admin-applications-tab">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            data-testid={`application-status-filter-${f.value || "all"}`}
            onClick={() => setStatus(f.value)}
            className={`text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full border-2 transition-colors ${status === f.value ? "bg-stranger-orange text-clean-white border-stranger-orange" : "border-soft-gray text-midnight-black/70"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <Input data-testid="application-search-input" placeholder="Search Stranger name, email, or session title..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
        <DateFilterPicker label="Applied From" testId="application-date-from-input" value={dateFrom} onChange={setDateFrom} />
        <DateFilterPicker label="Applied To" testId="application-date-to-input" value={dateTo} onChange={setDateTo} />
      </div>

      {applications === null ? (
        <p className="font-medium text-midnight-black/60">Loading applications...</p>
      ) : applications.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-applications">No applications match.</p>
      ) : (
        <div className="space-y-3" data-testid="applications-list">
          {applications.map((a) => (
            <div key={a.id} className="border border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`application-row-${a.id}`}>
              <div className="flex-1 min-w-[220px]">
                <p className="font-extrabold text-sm uppercase flex items-center gap-2">
                  {a.stranger_name || "Unknown Stranger"}
                  <StatusBadge status={a.status} testId={`application-status-${a.id}`} />
                </p>
                <p className="text-xs font-medium text-midnight-black/60">{a.stranger_email}</p>
                <p className="text-xs font-medium text-midnight-black/50">
                  Applied to: {a.session_title || "Unknown session"} · {formatDate(a.session_date)}
                </p>
                <p className="text-xs font-medium text-midnight-black/40">Consents to: {formatConsent(a.consent)}</p>
              </div>
              {a.status !== "cancelled" && (
                <Button variant="outline" data-testid={`cancel-application-${a.id}`} onClick={() => setConfirmCancel(a.id)} className="rounded-full font-bold text-xs">
                  CANCEL APPLICATION
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmCancel}
        onOpenChange={(v) => !v && setConfirmCancel(null)}
        title="Cancel this application?"
        description="If this Stranger was matched, their match partner will be released back to Registered."
        confirmLabel="CANCEL APPLICATION"
        destructive
        onConfirm={() => cancelApplication(confirmCancel)}
      />
    </div>
  );
};
